import React from 'react';
import { Typography, Container, Grid, Box, Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, useTheme, alpha } from '@mui/material';
import EffortMeter from '../../../assets/effort_meter.jpg';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import AssignmentIcon from '@mui/icons-material/Assignment';
import GroupIcon from '@mui/icons-material/Group';
import WorkIcon from '@mui/icons-material/Work';

const Guidelines = () => {
    const theme = useTheme();
    
    return (
        <Container maxWidth="lg" sx={{ py: 2 }}>
            {/* Header Section */}
            <Box sx={{ 
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                color: 'white',
                borderRadius: 3,
                p: 4,
                mb: 4,
                textAlign: 'center'
            }}>
                <TrendingUpIcon sx={{ fontSize: 48, mb: 2 }} />
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                    Self Reflection & Rise Guidelines
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    Building an Agile Mindset for Success
                </Typography>
            </Box>
            
            {/* Agile Mindset Card */}
            <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
                <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <EmojiObjectsIcon sx={{ fontSize: 32, color: theme.palette.primary.main, mr: 2 }} />
                        <Typography variant="h5" fontWeight="bold">
                            Understanding Agile Mindset
                        </Typography>
                    </Box>
                    <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                        Information Technology (IT) Companies are increasingly looking for people with an <strong>Agile mindset</strong>.
                        The word "Agile" means the ability to move quickly and easily.
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                        An agile mindset is the set of attitudes supporting an agile working environment.
                        These include <strong>respect</strong>, <strong>collaboration</strong>, <strong>improvement and learning cycles</strong>, 
                        <strong>pride in ownership</strong>, <strong>focus on delivering value</strong>, and the <strong>ability to adapt to change</strong>.
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                        This mindset is necessary to cultivate high-performing teams, who in turn deliver amazing value
                        for their customers.
                    </Typography>
                </CardContent>
            </Card>
            {/* Effort Meter Section */}
            <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
                <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <AssignmentIcon sx={{ fontSize: 32, color: theme.palette.primary.main, mr: 2 }} />
                        <Typography variant="h5" fontWeight="bold">
                            1. Effort Meter
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                        <Box sx={{ 
                            maxWidth: '100%', 
                            borderRadius: 2, 
                            overflow: 'hidden',
                            boxShadow: theme.shadows[3]
                        }}>
                            <img src={EffortMeter} alt="Effort-meter" style={{ maxWidth: '100%', height: '25rem', display: 'block' }} />
                        </Box>
                    </Box>
                </CardContent>
            </Card>
            {/* Scoring Table */}
            <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
                <CardContent sx={{ p: 4 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                        Scoring Reference Table
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                        Please refer to the below table to rate your effort level:
                    </Typography>
                    <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
                                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: '15%' }}>Score</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {[
                                    { score: 5, level: 'Exceptional Effort', desc: 'I worked really hard and cannot think of anything to make it better.' },
                                    { score: 4, level: 'Great Effort', desc: 'I think I can do one thing to make it better.' },
                                    { score: 3, level: 'Okay Effort', desc: 'but I can think of many things to make it better.' },
                                    { score: 2, level: 'Some Effort', desc: 'I completed the task but I have rushed through it.' },
                                    { score: 1, level: 'Little Effort', desc: 'I didn\'t finish.' },
                                    { score: 0, level: 'ZERO Effort', desc: 'I didn\'t try at all.' }
                                ].map((item, index) => (
                                    <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: alpha(theme.palette.grey[50], 0.5) } }}>
                                        <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: '1rem' }}>{item.score}</TableCell>
                                        <TableCell>
                                            <Box>
                                                <Typography component="span" fontWeight="bold" color="primary">
                                                    {item.level}:
                                                </Typography>
                                                <Typography component="span" sx={{ ml: 1 }}>
                                                    {item.desc}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
            {/* Key Components */}
            <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
                <CardContent sx={{ p: 4 }}>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                        Key Assessment Components
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, height: '100%' }}>
                                <CardContent>
                                    <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                                        2. Test Score
                                    </Typography>
                                    <Typography variant="body1">
                                        This represents the test score from weekly assessments and assignments.
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, height: '100%' }}>
                                <CardContent>
                                    <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                                        3. Comments
                                    </Typography>
                                    <Typography variant="body1">
                                        You could write your own comments to keep track of your progress.
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12}>
                            <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                                <CardContent>
                                    <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                                        4. Objective of this Metrics
                                    </Typography>
                                    <Typography variant="body1">
                                        To create responsible, accountable and hold trainees for their own growth.
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* FAQ Section */}
            <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
                <CardContent sx={{ p: 4 }}>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                        Frequently Asked Questions
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                        What is:
                    </Typography>
                    <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
                        <Table>
                            <TableBody>
                                {[
                                    {
                                        question: '1. My Logical Thinking Ability',
                                        answer: 'On a daily basis, are you able to improve your coding skills using appropriate logical thinking ability? Are you consistently practicing code to improve your logical thinking?'
                                    },
                                    {
                                        question: '2. Understanding of Assignments?',
                                        answer: 'Are you able to understand the assignments provided by the Mentors? Note: We are considering this as client requirements. In your job, you will be given written instructions that you need to follow to deliver your task.'
                                    },
                                    {
                                        question: '3. Usage of Coding Standards?',
                                        answer: 'Are you consistently following all the coding standards and guidelines provided by your Mentors? Note: This applies to every piece of code that you write.'
                                    },
                                    {
                                        question: '4. Usage of BitBucket (Check-Out & Check-In)?',
                                        answer: 'Are you consistently (every single day) following the defined BitBucket process?'
                                    },
                                    {
                                        question: '5. Code Review',
                                        answer: 'Industry Expert will be reviewing your code every week. Based on the comments you receive, please check with the Technology mentor to rate yourself in this criteria.'
                                    }
                                ].map((faq, index) => (
                                    <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: alpha(theme.palette.grey[50], 0.5) } }}>
                                        <TableCell sx={{ width: '35%', verticalAlign: 'top' }}>
                                            <Typography fontWeight="bold" color="primary">
                                                {faq.question}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                                                {faq.answer}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            {/* 21st Century Fluencies */}
            <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
                <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <GroupIcon sx={{ fontSize: 32, color: theme.palette.primary.main, mr: 2 }} />
                        <Typography variant="h5" fontWeight="bold">
                            21st Century Fluencies
                        </Typography>
                    </Box>
                    <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                        <CardContent>
                            <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                                Are you thinking from Other Person's Perspective?
                            </Typography>
                            <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                                When you have a conversation (verbal and written) with others, are you able to think from other person's perspective?
                                Are you building good working relationship with Mentors, colleagues and other people who are involved in your day-to-day life?
                            </Typography>
                        </CardContent>
                    </Card>
                </CardContent>
            </Card>

            {/* Work Ethics */}
            <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
                <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <WorkIcon sx={{ fontSize: 32, color: theme.palette.primary.main, mr: 2 }} />
                        <Typography variant="h5" fontWeight="bold">
                            Work Ethics
                        </Typography>
                    </Box>
                    <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
                        <Table>
                            <TableBody>
                                {[
                                    { aspect: 'Attendance', requirement: '100%' },
                                    { aspect: 'Timesheet', requirement: 'Ensure detailed information is filled out before you end your day (Every day)' },
                                    { aspect: 'Team Bonding', requirement: 'My ability to build good working relationship with my team members/peers.' },
                                    { aspect: 'Taking Responsibility to Deliver Assignments on Time', requirement: '100%.' }
                                ].map((item, index) => (
                                    <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: alpha(theme.palette.grey[50], 0.5) } }}>
                                        <TableCell sx={{ width: '40%', verticalAlign: 'top' }}>
                                            <Typography fontWeight="bold" color="primary">
                                                {item.aspect}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body1">
                                                {item.requirement}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

        </Container>
    );
};

export default Guidelines;